/**
 * /admin/mapa — MAPA DA OPERAÇÃO
 *
 * Três abas:
 *  1. Mapa visual — tráfego → página → captura → oferta → checkout/produto.
 *  2. Tabela de páginas — todas as rotas do site com função e indexação (CSV).
 *  3. Produtos & Webhook — catálogo Eduzz com o que conta como venda nova.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Download,
  ExternalLink,
  Map as MapIcon,
  RefreshCw,
  Search,
  Table as TableIcon,
  Tag,
} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { AdminShell } from "@/components/admin/shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getPublicSiteRoutes } from "@/lib/site-routes";
import {
  MAPA_FAMILIAS,
  MAPA_FLOWS,
  PRODUCT_CATEGORY_LABEL,
  TRAFFIC_LABEL,
  captureInfoFor,
  type MapaFlow,
} from "@/lib/mapa/catalog";
import {
  countedSalesSummary,
  listEduzzProducts,
  refreshEduzzCatalog,
  setEduzzProductActive,
} from "@/lib/mapa/products.functions";
import { downloadBlob } from "@/lib/download";

export const Route = createFileRoute("/admin/mapa")({
  head: () => ({
    meta: [
      { title: "Admin — Mapa da Operação" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/mapa">
      <MapaPage />
    </AdminGate>
  ),
});

function MapaPage() {
  return (
    <AdminShell
      title="Mapa da Operação"
      description="Tráfego, páginas, capturas, ofertas e checkouts — em uma só visão."
      icon={<MapIcon className="h-6 w-6 text-admin-accent" />}
      width="7xl"
    >
      <main>
        <Tabs defaultValue="mapa">
          <TabsList className="mb-6">
            <TabsTrigger value="mapa">Mapa visual</TabsTrigger>
            <TabsTrigger value="tabela">Páginas</TabsTrigger>
            <TabsTrigger value="produtos">Produtos &amp; Webhook</TabsTrigger>
          </TabsList>

          <TabsContent value="mapa">
            <MapaVisual />
          </TabsContent>
          <TabsContent value="tabela">
            <PaginasTable />
          </TabsContent>
          <TabsContent value="produtos">
            <ProdutosPanel />
          </TabsContent>
        </Tabs>
      </main>
    </AdminShell>
  );
}

/* ------------------------------------------------------------------ */
/* Aba 1 — mapa visual                                                */
/* ------------------------------------------------------------------ */

const COL = "rounded-lg border bg-background p-3 text-xs";

function FlowRow({ flow }: { flow: MapaFlow }) {
  const capture = captureInfoFor(flow);
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{flow.titulo}</h3>
        {flow.trafego.map((t) => (
          <Badge key={t} variant="secondary" className="text-[10px]">
            {TRAFFIC_LABEL[t]}
          </Badge>
        ))}
      </div>

      <div className="grid gap-2 lg:grid-cols-[1.2fr_auto_1.3fr_auto_1.1fr]">
        {/* Páginas */}
        <div className={COL}>
          <p className="mb-2 font-semibold uppercase tracking-wider text-muted-foreground">Páginas</p>
          <ul className="space-y-1.5">
            {flow.paginas.map((p) => (
              <li key={p.path + p.label}>
                <div className="flex items-center gap-1 font-medium">
                  {p.label}
                  {p.externa && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
                </div>
                <div className="break-all text-muted-foreground">{p.path}</div>
                <div className="text-muted-foreground/80">{p.papel}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden items-center justify-center lg:flex">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Captura */}
        <div className={COL}>
          <p className="mb-2 font-semibold uppercase tracking-wider text-muted-foreground">
            Captura &amp; qualificação
          </p>
          {capture ? (
            <div className="space-y-1.5">
              <p className="font-medium">{capture.entry.label}</p>
              <p className="text-muted-foreground">{capture.entry.camposObrigatorios}</p>
              <p className="text-muted-foreground">{capture.entry.qualificacao}</p>
              <div className="flex flex-wrap items-center gap-1 pt-1">
                <Tag className="h-3 w-3 text-muted-foreground" />
                <code className="rounded bg-muted px-1">{capture.tags.especifica}</code>
                {capture.tags.geral !== "—" && (
                  <code className="rounded bg-muted px-1">{capture.tags.geral}</code>
                )}
              </div>
              <p className="text-muted-foreground">CRM: {capture.crm.funil.join(" · ")}</p>
            </div>
          ) : (
            <p className="text-muted-foreground">{flow.capturaLivre ?? "Sem captura própria."}</p>
          )}
        </div>

        <div className="hidden items-center justify-center lg:flex">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Oferta + checkout */}
        <div className={COL}>
          <p className="mb-2 font-semibold uppercase tracking-wider text-muted-foreground">
            Oferta &amp; checkout
          </p>
          <ul className="space-y-1 text-muted-foreground">
            {flow.ofertas.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
          <div className="mt-2 space-y-1">
            {flow.checkouts.map((c) => (
              <div key={c.label} className="rounded bg-muted/60 px-2 py-1">
                <span className="font-medium">{c.label}</span>
                {c.code && <span className="ml-1 text-muted-foreground">· {c.code}</span>}
                {c.cupom && <span className="ml-1 text-primary">· {c.cupom}</span>}
              </div>
            ))}
          </div>
          <div className="mt-2 text-muted-foreground/90">
            <span className="font-semibold">Produtos: </span>
            {flow.produtos.join(" · ")}
          </div>
        </div>
      </div>

      {flow.obs && <p className="mt-3 text-xs text-muted-foreground italic">{flow.obs}</p>}
    </div>
  );
}

function MapaVisual() {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();

  const familias = useMemo(() => {
    if (!term) return MAPA_FAMILIAS;
    return MAPA_FAMILIAS.map((f) => ({
      ...f,
      fluxos: f.fluxos.filter((fl) =>
        JSON.stringify(fl).toLowerCase().includes(term),
      ),
    })).filter((f) => f.fluxos.length > 0);
  }, [term]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar página, produto, tag..."
            className="pl-9"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {MAPA_FLOWS.length} fluxos mapeados · derivados dos catálogos de rotas, capturas e checkouts.
        </p>
      </div>

      {familias.map((fam) => (
        <section key={fam.nome} className="space-y-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">{fam.nome}</h2>
            <p className="text-xs text-muted-foreground">{fam.descricao}</p>
          </div>
          <div className="space-y-3">
            {fam.fluxos.map((flow) => (
              <FlowRow key={flow.id} flow={flow} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Aba 2 — tabela de páginas                                          */
/* ------------------------------------------------------------------ */

function PaginasTable() {
  const routes = useMemo(() => getPublicSiteRoutes(), []);
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();

  const flowByPath = useMemo(() => {
    const m = new Map<string, MapaFlow>();
    for (const flow of MAPA_FLOWS) for (const p of flow.paginas) m.set(p.path, flow);
    return m;
  }, []);

  const rows = useMemo(
    () =>
      routes
        .map((r) => {
          const flow = flowByPath.get(r.path);
          return {
            path: r.externalUrl ?? r.path,
            label: r.label ?? "—",
            grupo: r.groupLabel,
            fluxo: flow?.titulo ?? "—",
            produtos: flow?.produtos.join(" · ") ?? "—",
            indexavel: r.indexable ? "pública" : "noindex",
          };
        })
        .filter((r) => !term || JSON.stringify(r).toLowerCase().includes(term)),
    [routes, flowByPath, term],
  );

  const exportCsv = () => {
    const head = ["url", "rotulo", "grupo", "fluxo", "produtos", "indexacao"];
    const csv = [
      head.join(","),
      ...rows.map((r) =>
        [r.path, r.label, r.grupo, r.fluxo, r.produtos, r.indexavel]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `mapa-operacao-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TableIcon className="h-4 w-4" /> Páginas da operação ({rows.length})
        </CardTitle>
        <div className="flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar..."
            className="h-9 w-44"
          />
          <Button size="sm" variant="outline" onClick={exportCsv}>
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b">
              <th className="py-2 pr-3">URL</th>
              <th className="py-2 pr-3">Rótulo</th>
              <th className="py-2 pr-3">Grupo</th>
              <th className="py-2 pr-3">Fluxo</th>
              <th className="py-2 pr-3">Produtos</th>
              <th className="py-2">SEO</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.path + r.label} className="border-b last:border-0">
                <td className="py-2 pr-3 font-mono break-all">{r.path}</td>
                <td className="py-2 pr-3">{r.label}</td>
                <td className="py-2 pr-3 text-muted-foreground">{r.grupo}</td>
                <td className="py-2 pr-3">{r.fluxo}</td>
                <td className="py-2 pr-3 text-muted-foreground">{r.produtos}</td>
                <td className="py-2">
                  <Badge variant={r.indexavel === "pública" ? "default" : "secondary"} className="text-[10px]">
                    {r.indexavel}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Aba 3 — produtos & webhook                                         */
/* ------------------------------------------------------------------ */

type ProductRow = {
  product_id: string;
  product_name: string;
  category: string;
  billing_type: string | null;
  checkout_code: string | null;
  is_active: boolean;
  sales_count: number;
  last_sale_at: string | null;
};

function ProdutosPanel() {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [renewals, setRenewals] = useState(0);
  const [summary, setSummary] = useState<{
    contabilizadas: number;
    renovacoes: number;
    inativas: number;
    receita: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        listEduzzProducts(),
        countedSalesSummary({ data: { days: 30 } }),
      ]);
      setRows(list.products as ProductRow[]);
      setRenewals(list.renewals);
      setSummary(sum);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar produtos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await refreshEduzzCatalog();
      toast.success(
        `${res.produtos} produtos (${res.novos} novos) · ${res.ativos} ativos · ${res.renovacoes_atualizadas} vendas remarcadas`,
      );
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const toggle = async (row: ProductRow, next: boolean) => {
    setRows((prev) => prev.map((r) => (r.product_id === row.product_id ? { ...r, is_active: next } : r)));
    try {
      await setEduzzProductActive({ data: { product_id: row.product_id, is_active: next } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao salvar");
      setRows((prev) => prev.map((r) => (r.product_id === row.product_id ? { ...r, is_active: !next } : r)));
    }
  };

  const term = q.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!onlyActive || r.is_active) &&
      (!term || r.product_name.toLowerCase().includes(term) || r.product_id.includes(term)),
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Como o webhook conta uma venda</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            A Eduzz envia TODAS as cobranças da conta para o webhook, incluindo faturas mensais de
            assinaturas de turmas antigas. Tudo continua sendo gravado (auditoria), mas uma venda só é
            contabilizada quando:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>o produto está marcado como <strong>ativo</strong> na lista abaixo; e</li>
            <li>
              a cobrança <strong>não é renovação</strong> — em assinaturas, apenas a primeira fatura do
              contrato conta ({renewals.toLocaleString("pt-BR")} cobranças já marcadas como renovação).
            </li>
          </ul>
          {summary && (
            <div className="grid gap-3 pt-2 sm:grid-cols-4">
              {[
                { k: "Vendas novas (30d)", v: summary.contabilizadas.toLocaleString("pt-BR") },
                { k: "Renovações ignoradas", v: summary.renovacoes.toLocaleString("pt-BR") },
                { k: "Produtos inativos", v: summary.inativas.toLocaleString("pt-BR") },
                {
                  k: "Receita contabilizada",
                  v: summary.receita.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
                },
              ].map((c) => (
                <div key={c.k} className="rounded-lg border bg-background p-3">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.k}</p>
                  <p className="mt-1 text-lg font-bold text-foreground">{c.v}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">Catálogo Eduzz ({filtered.length})</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={onlyActive} onCheckedChange={setOnlyActive} /> só ativos
            </label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar produto ou ID"
              className="h-9 w-52"
            />
            <Button size="sm" onClick={sync} disabled={syncing}>
              <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              Sincronizar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="py-6 text-sm text-muted-foreground">Carregando...</p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              Nenhum produto no catálogo. Clique em “Sincronizar” para importar do histórico de vendas.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b">
                  <th className="py-2 pr-3">Conta?</th>
                  <th className="py-2 pr-3">Produto</th>
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Categoria</th>
                  <th className="py-2 pr-3">Cobrança</th>
                  <th className="py-2 pr-3">Checkout</th>
                  <th className="py-2 pr-3">Pagas</th>
                  <th className="py-2">Última</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.product_id} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <Switch checked={r.is_active} onCheckedChange={(v) => void toggle(r, v)} />
                    </td>
                    <td className="py-2 pr-3 font-medium">{r.product_name}</td>
                    <td className="py-2 pr-3 font-mono text-muted-foreground">{r.product_id}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="secondary" className="text-[10px]">
                        {PRODUCT_CATEGORY_LABEL[r.category] ?? r.category}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {r.billing_type === "recurrence"
                        ? "assinatura"
                        : r.billing_type === "single"
                          ? "única"
                          : (r.billing_type ?? "—")}
                    </td>
                    <td className="py-2 pr-3 font-mono text-muted-foreground">{r.checkout_code ?? "—"}</td>
                    <td className="py-2 pr-3">{r.sales_count}</td>
                    <td className="py-2 text-muted-foreground">
                      {r.last_sale_at ? new Date(r.last_sale_at).toLocaleDateString("pt-BR") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
